import type { PublicDictionary } from "./types";

export const en: PublicDictionary = {
  meta: {
    title: "bizy — Point of Sales & Order Management",
    description:
      "A multi-tenant point of sales, order and inventory workspace for rental, catering and retail businesses. Run checkout, track down payments and share invoices in seconds.",
    keywords: "point of sales, POS, order management, inventory, invoice, rental, multi-tenant",
  },
  nav: {
    features: "Features",
    workflow: "How it works",
    pricing: "Pricing",
    signIn: "Sign in",
    dashboard: "Dashboard",
    startProject: "Start your project",
    language: "Language",
  },
  hero: {
    badge: "Built for rental, catering and retail",
    title: "Run the counter,",
    highlight: "not the spreadsheets.",
    description:
      "bizy keeps your catalog, checkout, down payments and invoices in one workspace — every order auditable, every invoice shareable.",
    primaryCta: "Start your project",
    secondaryCta: "Open dashboard",
    stats: [
      { value: "Full & DP", label: "Settlement modes" },
      { value: "A4 · Letter · 80mm", label: "Invoice layouts" },
      { value: "PNG + print", label: "Share-ready output" },
    ],
  },
  features: {
    title: "Everything the counter needs",
    description:
      "Products, variants, orders, payments and invoices — isolated per workspace, enforced by scopes.",
    items: [
      {
        title: "Multi-tenant by design",
        description: "Every workspace owns its products, orders, members and audit trail.",
      },
      {
        title: "Full or down payment",
        description: "Settle up front or take a DP and collect the balance later — totals stay exact.",
      },
      {
        title: "Granular roles",
        description: "Seven workspace roles, scope-checked on the server and in Postgres RLS.",
      },
      {
        title: "Audit everything",
        description: "Every create, update and delete writes a reviewable workspace event.",
      },
      {
        title: "Invoice images",
        description: "Render invoices on demand, store them, and share a stable link.",
      },
      {
        title: "Print ready",
        description: "A4, Letter or 80mm thermal — the layout follows the page size you pick.",
      },
    ],
  },
  workflow: {
    title: "From cart to settled balance",
    description: "One flow, whether the customer pays in full or leaves a deposit.",
    steps: [
      { title: "Pick products", description: "Select a base product and its variant, then set the quantity." },
      { title: "Choose settlement", description: "Take the full amount now, or record a down payment." },
      { title: "Issue the invoice", description: "Generate the invoice image and share it via WhatsApp or email." },
      { title: "Collect the rest", description: "Reopen the order, settle the balance, issue the remaining invoice." },
    ],
  },
  plans: {
    title: "Plans",
    description: "Start free. Pro unlocks unlimited products, orders and teammates.",
    free: {
      title: "Free",
      description: "Single user, 25 products, 20 orders a day, single-variant products.",
    },
    pro: {
      title: "Pro",
      description: "Unlimited products and orders, teammates, custom variants and advanced reports.",
    },
    cta: "Choose plan",
    comingSoon: "Checkout opens soon",
  },
  cta: {
    title: "Open your workspace today",
    description: "Sign in with Google, pick a plan, and start issuing invoices in minutes.",
    primary: "Start your project",
    secondary: "Sign in",
  },
  footer: {
    tagline: "Point of sales, orders and inventory — in one workspace.",
    rights: "All rights reserved.",
    product: "Product",
    resources: "Resources",
    legal: "Legal",
  },
  auth: {
    title: "Sign in to bizy",
    description: "Continue with Google to open your workspace dashboard.",
    google: "Continue with Google",
    disclaimer: "By continuing you agree to the workspace terms and privacy policy.",
    retry: "Try again",
  },
  invoice: {
    title: "Invoice",
    billTo: "Billed to",
    invoiceNumber: "Invoice number",
    issuedAt: "Issued",
    dueAt: "Due",
    qty: "Qty",
    price: "Price",
    amount: "Amount",
    subtotal: "Subtotal",
    tax: "Tax",
    total: "Total",
    paid: "Paid",
    balance: "Balance",
    status: "Status",
    pageSize: "Page size",
    print: "Print",
    download: "Download",
    shareWhatsApp: "Share to WhatsApp",
    shareEmail: "Share by email",
    settlementType: "Settlement",
    fullSettlement: "Paid in full",
    downPayment: "Down payment",
    balanceInvoice: "Balance invoice",
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    unauthorized: "You need to sign in to continue.",
    notFound: "We could not find what you were looking for.",
  },
};
